/**
 * Choisir le tiers d'une ligne d'ouverture — fournisseur, organisme
 * subsidiant, CPAS — ou le créer s'il n'existe pas encore.
 *
 * Les familles n'y figurent pas : leurs créances et crédits d'ouverture sont
 * calculés, jamais saisis. Un tiers nomme la contrepartie ; il n'ouvre jamais
 * de compte de grand livre (I-41).
 */

import { useEffect, useState } from 'react';
import { Loader2, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { messageErreur } from '../../../lib/payments/erreur';
import {
  chercherDesTiers,
  creerUnTiers,
  lireLesTypesDeTiers,
  lireUnTiers,
  type TiersTrouve,
  type TypeDeTiers,
} from '../../../lib/compta/reprise/gateway.supabase';

export default function ChoixDuTiers({ tiersId, onChange, typeSuggere, horsCdv = false }: {
  tiersId: string | null;
  onChange: (id: string | null, typeCode: string | null) => void;
  typeSuggere: string;
  /** Seulement des tiers sans compte CDV (clients et acomptes clients saisis). */
  horsCdv?: boolean;
}) {
  const [choisi, setChoisi] = useState<TiersTrouve | null>(null);
  const [texte, setTexte] = useState('');
  const [trouves, setTrouves] = useState<TiersTrouve[]>([]);
  const [recherche, setRecherche] = useState(false);
  const [types, setTypes] = useState<TypeDeTiers[]>([]);
  const [creation, setCreation] = useState(false);
  const [type, setType] = useState(typeSuggere);

  useEffect(() => {
    if (!tiersId) { setChoisi(null); return; }
    if (choisi?.id === tiersId) return;
    lireUnTiers(tiersId).then(setChoisi).catch((e) => toast.error(messageErreur(e)));
  }, [tiersId, choisi?.id]);

  useEffect(() => {
    if (choisi) return;
    const t = texte.trim();
    if (t.length < 2) { setTrouves([]); return; }
    let vivant = true;
    setRecherche(true);
    const minuterie = setTimeout(() => {
      chercherDesTiers(t, horsCdv)
        .then((r) => { if (vivant) setTrouves(r); })
        .catch((e) => toast.error(messageErreur(e)))
        .finally(() => { if (vivant) setRecherche(false); });
    }, 250);
    return () => { vivant = false; clearTimeout(minuterie); };
  }, [texte, choisi, horsCdv]);

  useEffect(() => { setType(typeSuggere); }, [typeSuggere]);

  const ouvrirCreation = async () => {
    setCreation(true);
    if (types.length === 0) {
      try { setTypes(await lireLesTypesDeTiers()); } catch (e) { toast.error(messageErreur(e)); }
    }
  };

  const creer = async () => {
    try {
      const t = await creerUnTiers(type, texte);
      toast.success(`Tiers « ${t.nom} » créé`);
      setChoisi(t);
      setCreation(false);
      onChange(t.id, t.typeCode);
    } catch (e) {
      toast.error(messageErreur(e));
    }
  };

  if (choisi) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="rounded bg-gray-100 px-2 py-1">{choisi.nom} <span className="text-xs text-gray-500">({choisi.typeCode})</span></span>
        <button type="button" aria-label="Changer de tiers" onClick={() => { setChoisi(null); onChange(null, null); setTexte(''); }}
          className="text-gray-400 hover:text-gray-700"><X className="h-4 w-4" /></button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Nom du tiers (2 lettres au moins)"
          className="w-72 rounded border border-gray-300 px-2 py-1 text-sm" />
        {recherche && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>
      {trouves.length > 0 && (
        <ul className="max-h-48 w-72 overflow-y-auto rounded border border-gray-200 bg-white text-sm shadow-sm">
          {trouves.map((t) => (
            <li key={t.id}>
              <button type="button" onClick={() => { setChoisi(t); onChange(t.id, t.typeCode); }}
                className="w-full px-2 py-1 text-left hover:bg-indigo-50">
                {t.nom} <span className="text-xs text-gray-500">({t.typeCode})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {texte.trim().length >= 2 && !recherche && (
        creation ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Créer « {texte.trim()} » comme</span>
            <select value={type} onChange={(e) => setType(e.target.value)} className="rounded border border-gray-300 px-2 py-1 text-sm">
              {types.map((t) => <option key={t.code} value={t.code}>{t.libelle}</option>)}
            </select>
            <button type="button" onClick={creer} className="rounded bg-indigo-600 px-2 py-1 text-white hover:bg-indigo-700">Créer</button>
            <button type="button" onClick={() => setCreation(false)} className="text-gray-600 hover:underline">Annuler</button>
          </div>
        ) : (
          <button type="button" onClick={ouvrirCreation} className="inline-flex items-center gap-1 text-sm text-indigo-700 hover:underline">
            <Plus className="h-4 w-4" /> Créer un nouveau tiers « {texte.trim()} »
          </button>
        )
      )}
    </div>
  );
}
